import { defineStore } from "pinia";
import { PageState, usePageStore } from "@rmp/core/stores/composables/page/usePageStore";
import AbortService from "@rmp/core/services/abortService";
import { FormState, useFormStore } from "@rmp/core/stores/composables/form/useFormStore";
import { AuthAppController } from "@rmp/id/api/auth";
import { AuthPageModeType } from "@rmp/core/types/authorization/authPageModeType";
import { secondsToMilliseconds } from "date-fns";
import router from "@rmp/id/router";
import { RouteNames } from "@rmp/id/router/routes";
import { useAppStore } from "@rmp/core/stores/appStore";

const abortService = new AbortService();
const authAppController = new AuthAppController(abortService);

const page = usePageStore(abortService);
const form = useFormStore();

export interface ResetPasswordState extends PageState, FormState {
	password: string;
	mode: AuthPageModeType;
	secondsUntilRedirectToAuth: number;
}

const DEFAULT_SECONDS_UNTIL_REDIRECT_TO_AUTH = 9;

const getDefaultState = (): ResetPasswordState => {
	return {
		...page.getDefaultPageState(),
		...form.getDefaultState(),
		password: "",
		mode: AuthPageModeType.INITIAL,
		secondsUntilRedirectToAuth: DEFAULT_SECONDS_UNTIL_REDIRECT_TO_AUTH
	};
};

export const useResetPasswordStore = defineStore({
	id: "id-reset-password",
	state: (): ResetPasswordState => getDefaultState(),
	getters: {
		...page.getters,
		...form.getters
	},
	actions: {
		...page.actions,
		...form.actions,
		async beforeInitialized() {
		},
		async save() {
			this.formSaving = true;

			try {
				const query = new URLSearchParams(window.location.search);
				
				await authAppController.resetPassword({
					userId: query.get("userId") ?? "",
					code: query.get("code") ?? ""
				}, { password: this.password });

				this.mode = AuthPageModeType.SUCCESS;
				
				this.startRedirectToAuthHandler();
			} catch (error: any) {
				useAppStore().handleActionError(error);
			} finally {
				this.formSaving = false;
			}
		},
		startRedirectToAuthHandler() {
			const id = setInterval(async () => {
				if(this.secondsUntilRedirectToAuth <= 1) {
					clearInterval(id);
					
					await router.push({ name: RouteNames.SIGN_IN });
				} else {
					this.secondsUntilRedirectToAuth--;
				}
			}, secondsToMilliseconds(1));
		}
	}
});
